import { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
// @ts-expect-error Could not find a declaration file for module PropertyGroupRecord
import PropertyGroupRecord from 'customer-data-objects/property/PropertyGroupRecord';
import { useFieldOptions } from '../../../hooks/useFieldOptions';
import SegmentationFiltersInstanceContext from '../../../state/SegmentationFiltersInstanceContext';
import DataExplorerEmptyState from './general/DataExplorerEmptyState';
import styled from 'foundations-theming/utils';
import { AutoSizer, List as VirtualizedList, CellMeasurerCache, CellMeasurer } from 'react-virtualized';
import EditorFieldOption from '../../editor/EditorFieldOption';
import EditorFieldLoadMoreOption from '../../editor/EditorFieldLoadMoreOption';
import { PropertySearchSuggestions } from '../../editor/NLPSuggestions/PropertySearchSuggestions';
import { hasSemanticPropertySearch } from '../../../permissions/gates';
import { useNLPIntegration } from '../../../hooks/useNLPIntegration';
import { CATEGORY_KEYS } from '../categorySelection/Categories';
import { jsx as _jsx } from "react/jsx-runtime";
const VirtualizedListContainer = styled.div.withConfig({
  displayName: "FilterFamilySelectOptions__VirtualizedListContainer",
  componentId: "sdmfwj-0"
})(["height:100%;width:100%;"]);
const FilterFamilySelectOptions = props => {
  const {
    filterFamily,
    isLoadingFields,
    searchString,
    fieldTypeFilter
  } = props;
  const {
    isLoadingFields: isLoadingFieldsInitial,
    selectedCategory
  } = useContext(SegmentationFiltersInstanceContext);
  const [loadingMore, setLoadingMore] = useState(false);
  const {
    options: fieldOptions
  } = useFieldOptions(filterFamily, searchString, undefined, fieldTypeFilter);
  const isPropertiesCategory = selectedCategory === CATEGORY_KEYS.PROPERTIES;
  const {
    showNLPSuggestions,
    handleOnSelectField,
    suggestedPropertiesResultsRef
  } = useNLPIntegration({
    isEnabled: hasSemanticPropertySearch() && isPropertiesCategory,
    searchString,
    filterFamily
  });
  const prevOptionsLength = useRef(fieldOptions.length);
  const virtualizedListRef = useRef(null);
  const cellMeasurerCache = useMemo(() => {
    return new CellMeasurerCache({
      defaultHeight: 40,
      fixedWidth: true
    });
  }, []);
  const remeasureNLPSuggestionsRow = useCallback(() => {
    if (hasSemanticPropertySearch() && isPropertiesCategory && searchString) {
      var _virtualizedListRef$c;
      cellMeasurerCache.clear(0, 0);
      (_virtualizedListRef$c = virtualizedListRef.current) === null || _virtualizedListRef$c === void 0 || _virtualizedListRef$c.recomputeRowHeights(0);
    }
  }, [searchString, cellMeasurerCache, isPropertiesCategory]);

  // This useEffect tracks the number of field options to set keep the loading
  // state updated
  useEffect(() => {
    if (prevOptionsLength.current < fieldOptions.length && loadingMore) {
      setLoadingMore(false);
    }
    // When options or search string change, invalidate cache and recompute heights
    if (virtualizedListRef.current) {
      cellMeasurerCache.clearAll();
      virtualizedListRef.current.recomputeRowHeights();
    }
    prevOptionsLength.current = fieldOptions.length;
  }, [fieldOptions, searchString, loadingMore, cellMeasurerCache]);
  if (isLoadingFieldsInitial || isLoadingFields) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "newCustomerDataFilters.fieldSelectFlow.loadingFields"
      }),
      showLabel: true,
      size: "small"
    });
  }
  const hasNoOptions = fieldOptions.length === 0 && !showNLPSuggestions;
  if (hasNoOptions) {
    return /*#__PURE__*/_jsx(DataExplorerEmptyState, {
      message: "customerDataFiltersUI.dataExplorer.emptyState.noOptions"
    });
  }
  const nlpSuggestionsRowCount = showNLPSuggestions ? 1 : 0;
  const rowCount = nlpSuggestionsRowCount + fieldOptions.length + 1; // +1 for EditorFieldLoadMoreOption

  const rowRenderer = ({
    index,
    key,
    style,
    parent
  }) => {
    let content;
    if (showNLPSuggestions && index === 0) {
      content = /*#__PURE__*/_jsx(PropertySearchSuggestions, {
        filterFamily: filterFamily,
        searchString: searchString,
        onSelectField: handleOnSelectField,
        suggestedPropertiesResultsRef: suggestedPropertiesResultsRef,
        onLayoutMayHaveChanged: remeasureNLPSuggestionsRow,
        useDataExplorerStyling: true
      });
    } else if (index < nlpSuggestionsRowCount + fieldOptions.length) {
      const optionIndex = index - nlpSuggestionsRowCount;
      const entry = fieldOptions[optionIndex];
      if (entry instanceof PropertyGroupRecord) {
        content = /*#__PURE__*/_jsx("b", {
          children: /*#__PURE__*/_jsx(TruncateString, {
            paddingInlineStart: "400",
            children: entry.get('displayName')
          })
        }, `${index}-${entry.get('name')}`);
      } else {
        content = /*#__PURE__*/_jsx(EditorFieldOption, {
          field: entry,
          filterFamily: filterFamily,
          onClick: () => handleOnSelectField(entry)
        }, `${index}-${entry.get('name')}`);
      }
    } else {
      // Last row is the load more button
      content = /*#__PURE__*/_jsx(EditorFieldLoadMoreOption, {
        filterFamily: filterFamily,
        isLoadingMore: loadingMore,
        onLoadMore: setLoadingMore,
        searchString: searchString
      });
    }
    return /*#__PURE__*/_jsx(CellMeasurer, {
      cache: cellMeasurerCache,
      columnIndex: 0,
      parent: parent,
      rowIndex: index,
      children: ({
        registerChild
      }) =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized: style is coming from react-virtualized
      _jsx("div", {
        ref: registerChild,
        style: style,
        children: content
      })
    }, key);
  };
  return /*#__PURE__*/_jsx(VirtualizedListContainer, {
    children: /*#__PURE__*/_jsx(AutoSizer, {
      children: ({
        height,
        width
      }) => /*#__PURE__*/_jsx(VirtualizedList, {
        ref: virtualizedListRef,
        deferredMeasurementCache: cellMeasurerCache,
        height: height,
        width: width,
        overscanRowCount: 5,
        rowCount: rowCount,
        rowHeight: cellMeasurerCache.rowHeight,
        rowRenderer: rowRenderer
      })
    })
  });
};
export default FilterFamilySelectOptions;